import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { ServiceImageSlideshow } from "@/components/ServiceImageSlideshow";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BookingModal } from "@/components/BookingModal";
import { Clock, DollarSign, Image } from "lucide-react";
import { useCurrency } from "@/hooks/use-currency";
import { useTranslation } from "react-i18next";
import { LocalizedService } from "@/types";
import { useLocalized } from "@/hooks/useLocalized";

// Interface imported from types
type Service = LocalizedService;

const Services = () => {
  const { t } = useTranslation();
  const { getLocalized } = useLocalized();
  const { formatPrice } = useCurrency();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const { data, error } = await supabase
        .from("services")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("category");

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookService = (service: Service) => {
    setSelectedService(service);
    setBookingModalOpen(true);
  };

  // Group services by category
  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {} as Record<string, Service[]>);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 container mx-auto px-4 py-8 md:py-16">
          <div className="text-center mb-8 md:mb-12">
            <Skeleton className="h-10 md:h-12 w-48 md:w-64 mx-auto mb-4" />
            <Skeleton className="h-6 w-full max-w-2xl mx-auto" />
          </div>
          <div className="grid gap-8 md:gap-12">
            {[1, 2].map((i) => (
              <div key={i}>
                <Skeleton className="h-8 w-32 md:w-48 mb-4" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {[1, 2, 3].map((j) => (
                    <Skeleton key={j} className="h-80" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <main className="flex-1 container mx-auto px-4 py-8 md:py-16">
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">{t('services.title', 'Our Services')}</h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('services.subtitle', 'Professional photography services tailored to capture your special moments')}
          </p>
        </div>

        <div className="space-y-12 md:space-y-16">
          {Object.entries(groupedServices).map(([category, categoryServices]) => (
            <section key={category}>
              <div className="mb-6 md:mb-8">
                <h2 className="text-2xl md:text-3xl font-bold capitalize mb-2">{t(`category.${category}`, category)}</h2>
                <div className="h-1 w-20 bg-[hsl(var(--gold))]" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {categoryServices.map((service) => (
                  <Card key={service.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    {service.media_urls && service.media_urls.length > 0 && (
                      <div
                        className="relative h-48 cursor-pointer group"
                        onClick={() => setSelectedImage(service.media_urls[0])}
                      >
                        <ServiceImageSlideshow
                          images={service.media_urls}
                          alt={getLocalized(service.name)}
                          className="h-full group-hover:scale-105 transition-transform duration-300"
                        />
                        {service.media_urls.length > 1 && (
                          <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center gap-1 z-10">
                            <Image className="h-3 w-3" />
                            {service.media_urls.length}
                          </div>
                        )}
                      </div>
                    )}

                    <CardHeader>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <CardTitle className="text-xl">{getLocalized(service.name)}</CardTitle>
                        <Badge variant="outline" className="capitalize shrink-0">
                          {t(`category.${service.category}`, service.category)}
                        </Badge>
                      </div>
                      <CardDescription className="line-clamp-2">
                        {getLocalized(service.description)}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{service.duration_minutes} {t('common.minutes', 'minutes')}</span>
                      </div>
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <DollarSign className="h-5 w-5 text-[hsl(var(--gold))]" />
                        <span>{formatPrice(service.base_price)}</span>
                      </div>
                    </CardContent>

                    <CardFooter className="flex gap-2">
                      <Button
                        className="flex-1"
                        onClick={() => handleBookService(service)}
                      >
                        {t('services.bookNow', 'Book Now')}
                      </Button>
                      {service.media_urls && service.media_urls.length > 1 && (
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSelectedService(service);
                            setSelectedImage(service.media_urls[0]);
                          }}
                        >
                          {t('services.viewGallery', 'View Gallery')}
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>

      <Footer />

      {/* Image Gallery Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setSelectedImage(null);
            setSelectedService(null);
          }}
        >
          <div className="relative max-w-7xl w-full">
            <Button
              variant="outline"
              size="sm"
              className="absolute top-4 right-4 z-10"
              onClick={() => {
                setSelectedImage(null);
                setSelectedService(null);
              }}
            >
              {t('common.close', 'Close')}
            </Button>
            <img
              src={selectedImage}
              alt="Service"
              className="max-w-full max-h-[90vh] object-contain mx-auto"
            />

            {/* Thumbnail Navigation */}
            {selectedService && selectedService.media_urls.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/70 p-2 rounded-lg">
                {selectedService.media_urls.map((url, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(url);
                    }}
                    className={`w-16 h-16 rounded overflow-hidden border-2 transition-all ${selectedImage === url
                      ? 'border-[hsl(var(--gold))] scale-110'
                      : 'border-white/30 opacity-70 hover:opacity-100'
                      }`}
                  >
                    <img
                      src={url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Booking Modal */}
      <BookingModal
        isOpen={bookingModalOpen}
        onClose={() => {
          setBookingModalOpen(false);
          setSelectedService(null);
        }}
        service={selectedService}
      />
    </div>
  );
};

export default Services;
